
import { Address } from '../types';
import { userRepository } from '../../data/userRepository';
import { logger } from '../../infrastructure/logger';

export const userProfileService = {
    
    // --- ADDRESSES ---
    async getAddresses(userId?: string): Promise<Address[]> {
        if (userId) {
            const user = await userRepository.getUserById(userId);
            return user?.addresses || [];
        }
        // Guest fallback
        const saved = localStorage.getItem('alltuc_guest_addresses');
        return saved ? JSON.parse(saved) : [];
    },

    async addAddress(userId: string | undefined, addressData: Partial<Address>): Promise<{ success: boolean; message?: string }> {
        try {
            const currentAddresses = await this.getAddresses(userId);

            if (currentAddresses.length >= 5) {
                return { success: false, message: 'Límite de 5 direcciones alcanzado' };
            }

            if (!addressData.address || !addressData.city || !addressData.name) {
                return { success: false, message: 'Faltan datos obligatorios' };
            }

            const newAddress: Address = {
                id: Date.now(),
                name: addressData.name,
                address: addressData.address,
                city: addressData.city,
                zipCode: addressData.zipCode || '4000',
                isDefault: currentAddresses.length === 0,
                type: addressData.type || 'other'
            };

            if (userId) {
                await userRepository.addAddress(userId, newAddress);
            } else {
                const updated = [...currentAddresses, newAddress];
                localStorage.setItem('alltuc_guest_addresses', JSON.stringify(updated));
            }

            return { success: true };
        } catch (err) {
            logger.error('[userProfileService] Error adding address', err);
            return { success: false, message: 'Error al agregar dirección' };
        }
    },

    async removeAddress(userId: string | undefined, addressId: number): Promise<{ success: boolean }> {
        try {
            const currentAddresses = await this.getAddresses(userId);
            const addressToRemove = currentAddresses.find(a => a.id === addressId);
            
            if (!addressToRemove) return { success: true };

            if (userId) {
                await userRepository.removeAddress(userId, addressToRemove);
            } else {
                const updated = currentAddresses.filter(a => a.id !== addressId);
                localStorage.setItem('alltuc_guest_addresses', JSON.stringify(updated));
            }
            return { success: true };
        } catch (err) {
            logger.error('[userProfileService] Error removing address', err);
            return { success: false };
        }
    },

    async setDefaultAddress(userId: string | undefined, addressId: number): Promise<{ success: boolean }> {
        try {
            const currentAddresses = await this.getAddresses(userId);
            const updatedAddresses = currentAddresses.map(a => ({
                ...a,
                isDefault: a.id === addressId
            }));

            if (userId) {
                // This remains a full overwrite because isDefault is a property of every item
                await userRepository.updateAddresses(userId, updatedAddresses);
            } else {
                localStorage.setItem('alltuc_guest_addresses', JSON.stringify(updatedAddresses));
            }
            return { success: true };
        } catch (err) {
            logger.error('[userProfileService] Error setting default address', err);
            return { success: false };
        }
    },

    // --- FAVORITES (Firestore Persistence) ---
    
    async getFavorites(userId: string): Promise<string[]> {
        if (!userId) return [];
        const user = await userRepository.getUserById(userId);
        return user?.favorites || [];
    },

    async toggleFavorite(userId: string, productId: string): Promise<{ success: boolean; isFavorite: boolean }> {
        if (!userId) return { success: false, isFavorite: false };

        try {
            const currentFavs = await this.getFavorites(userId);
            const exists = currentFavs.includes(productId);
            
            if (exists) {
                await userRepository.removeFavorite(userId, productId);
            } else {
                await userRepository.addFavorite(userId, productId);
            }
            
            return { success: true, isFavorite: !exists };
        } catch (error) {
            logger.error('[UserProfileService] Error toggling favorite', error);
            return { success: false, isFavorite: false };
        }
    },

    // --- PROFILE MANAGEMENT ---
    async updateProfile(userId: string, data: { name: string; dni: string; phone: string }): Promise<{ success: boolean; message?: string }> {
        try {
            // Validation
            if (!data.name || data.name.length < 3) return { success: false, message: 'Nombre inválido.' };
            
            const cleanDni = data.dni.replace(/[^0-9]/g, '');
            if (cleanDni.length < 7 || cleanDni.length > 9) return { success: false, message: 'DNI inválido.' };
            
            const cleanPhone = data.phone.replace(/[^0-9]/g, '');
            if (cleanPhone.length < 10) return { success: false, message: 'Teléfono inválido (mín. 10 dígitos).' };

            // Check DNI Uniqueness if changed (this implies fetching user first, or trusted client check. 
            // For now, simple check: if existing DNI belongs to someone else.
            // Optimized: Let repository handle it or check here.
            const existingUser = await userRepository.getUserByDni(cleanDni);
            if (existingUser && existingUser.id !== userId) {
                return { success: false, message: 'Este DNI ya está registrado en otra cuenta.' };
            }

            await userRepository.updateUser(userId, {
                name: data.name,
                dni: cleanDni,
                phone: cleanPhone
            });

            return { success: true, message: 'Perfil actualizado' };
        } catch (err) {
            return { success: false, message: 'Error al actualizar perfil' };
        }
    }
};
